import { invalidateSessionFeedback } from '@heidi/session/data';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  submitChatFeedback,
  type ChatFeedbackRequest,
} from '@/pages/api/scribeAPIV2';
import { FeedbackTargetType } from '@heidi/schema/ml-scribe';
import { useMutation } from '@tanstack/react-query';
import { track } from '@/lib/analytics/track';

const CHAT_FEEDBACK_MUTATION_KEY = 'CHAT_FEEDBACK_MUTATION_KEY';

/**
 * Mutation hook for submitting chat (Evidence) feedback via POST /chat/feedback.
 *
 * Unlike `useBinaryFeedbackMutation`, this hook:
 * - Does NOT require a session ID from the URL (works for sessionless chats)
 * - Sends `target_id` (chat ObjectId) and optional `session_id` as top-level fields
 * - Supports `message_id` for per-message feedback (PR 2)
 */
export function useChatFeedbackMutation() {

  return useMutation({
    mutationKey: [CHAT_FEEDBACK_MUTATION_KEY],
    mutationFn: (request: ChatFeedbackRequest) => {
      return submitChatFeedback(request);
    },
    onSuccess: (_, variables) => {
      // Invalidate session feedback cache if a session_id was provided
      if (variables.session_id) {
        void invalidateSessionFeedback(variables.session_id);
      }

      const thumbsValue =
        variables.feedback.rating === 'thumb_up'
          ? 'up'
          : variables.feedback.rating === 'thumb_down'
            ? 'down'
            : undefined;

      // Track binary feedback submission (same events as useBinaryFeedbackMutation)
      if (variables.feedback.user_feedback) {
        track({ options: { amplitude: false },
          event_name: TrackingEventName.FeedbackSubmitted,
          props: {
            type: 'qualitative',
            additional_thumbs_feedback: thumbsValue,
            target_id: variables.target_id,
            target_type: FeedbackTargetType.EVIDENCE,
            session_id: variables.session_id ?? '',
            value: variables.feedback.user_feedback,
          },
        });
      } else if (thumbsValue !== undefined) {
        track({ options: { amplitude: false },
          event_name: TrackingEventName.FeedbackSubmitted,
          props: {
            type: 'thumbs',
            target_id: variables.target_id,
            target_type: FeedbackTargetType.EVIDENCE,
            session_id: variables.session_id ?? '',
            value: thumbsValue,
          },
        });
      }
    },
  });
}
